import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import api from '../../utils/api';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableHead, TableRow, Button, TextField, Select, MenuItem, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControl, InputLabel, Chip, Tooltip, Snackbar, Alert, CircularProgress
} from '@mui/material';
import { CheckCircle, Cancel, Edit, Visibility, Payment as PaymentIcon, History as HistoryIcon } from '@mui/icons-material';
import { states, cities } from './data/Doctorstatecity.js';
import { doctorSpecializations } from './data/doctorSpecializations.js'; // Import specializations

const DoctorManagement = () => {
  const [doctors, setDoctors] = useState([]);
  const [pendingDoctors, setPendingDoctors] = useState([]);
  const [doctorStats, setDoctorStats] = useState({ activeDoctors: 0, deactivatedDoctors: 0, deletedDoctors: 0 });
  const [search, setSearch] = useState({
    name: '',
    specialization: '',
    state: '',
    city: '',
    status: 'all',
    sortBy: 'name'
  });
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [openDialog, setOpenDialog] = useState({ approve: false, reject: false, edit: false });
  const [rejectionReason, setRejectionReason] = useState('');
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { adminUser } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch all necessary data
  const fetchData = async () => {
    if (!adminUser) return;
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    setLoading(true);
    try {
      const [doctorsRes, pendingRes, statsRes] = await Promise.all([
        api.get('/admin/users', config),
        api.get('/admin/doctors/pending', config),
        api.get('/admin/doctors/stats', config),
      ]);
      setDoctors(doctorsRes.data.doctors);
      setPendingDoctors(pendingRes.data || []);
      setDoctorStats(statsRes.data);
    } catch (err) {
      console.error('Doctor fetch error:', err.response?.data);
      setSnackbar({
        open: true,
        message: 'Failed to fetch doctor data',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [adminUser]);

  const filteredDoctors = doctors
    .filter((doc) => {
      return (
        doc.name.toLowerCase().includes(search.name.toLowerCase()) &&
        (!search.specialization || doc.specialization === search.specialization) &&
        (!search.state || doc.state === search.state) &&
        (!search.city || doc.city === search.city) &&
        (search.status === 'all' || (search.status === 'verified' ? doc.isVerified : !doc.isVerified))
      );
    })
    .sort((a, b) => {
      if (search.sortBy === 'name') return a.name.localeCompare(b.name);
      if (search.sortBy === 'consultations') return (b.appointmentCount || 0) - (a.appointmentCount || 0);
      if (search.sortBy === 'earnings') return (b.totalEarnings || 0) - (a.totalEarnings || 0);
      return 0;
    });

  const getAvailableCities = () => {
    if (search.state) {
      return cities[search.state] || [];
    }
    return Object.values(cities).flat();
  };

  const handleApproveReject = async (id, action) => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      let response;
      if (action === 'approve') {
        response = await api.put(`/admin/doctors/verify/${id}`, {}, config);
      } else {
        response = await api.post(`/admin/doctors/reject/${id}`, { reason: rejectionReason }, config);
      }

      setPendingDoctors(pendingDoctors.filter((doc) => doc._id !== id));
      if (action === 'approve') {
        setDoctors(doctors.map((doc) => (doc._id === id ? { ...doc, isVerified: true } : doc)));
      }

      setSnackbar({
        open: true,
        message: response.data.message || `Doctor ${action === 'approve' ? 'successfully verified' : 'rejected and notified'}`,
        severity: 'success'
      });
      setOpenDialog({ ...openDialog, [action]: false });
      setRejectionReason('');
    } catch (err) {
      console.error(`${action} error:`, err.response?.data);
      setSnackbar({
        open: true,
        message: err.response?.data?.error || `Failed to ${action} doctor`,
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async () => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    try {
      await api.put(`/admin/users/${selectedDoctor._id}`, editForm, config);
      setDoctors(doctors.map((doc) => (doc._id === selectedDoctor._id ? { ...doc, ...editForm } : doc)));
      setSnackbar({ open: true, message: 'Doctor profile updated successfully', severity: 'success' });
      setOpenDialog({ ...openDialog, edit: false });
    } catch (err) {
      console.error('Edit error:', err.response?.data);
      setSnackbar({ open: true, message: 'Failed to update doctor profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handlePayout = async (doctorId) => {
    setLoading(true);
    const config = { headers: { Authorization: `Bearer ${adminUser.accessToken}` } };
    const doctor = doctors.find(doc => doc._id === doctorId);
    const pendingPayout = doctor.pendingPayout || 0;

    if (pendingPayout <= 0) {
      setSnackbar({ open: true, message: 'No pending amount available for payout', severity: 'warning' });
      setLoading(false);
      return;
    }

    try {
      const response = await api.put(`/admin/withdrawals/approve/${doctorId}`, {
        transactionId: `TXN_${Date.now()}`,
        paymentMode: 'Bank Transfer',
        paidAmount: pendingPayout,
        dateOfPayment: new Date().toISOString(),
      }, config);

      setDoctors(doctors.map((doc) =>
        doc._id === doctorId ? { ...doc, pendingPayout: 0 } : doc
      ));
      setSnackbar({ open: true, message: 'Payout processed successfully', severity: 'success' });
    } catch (err) {
      console.error('Payout error:', err.response?.data);
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to process payout', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDocument = async (doctorId, documentType) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/admin/doctors/${doctorId}/documents/${documentType}`,
        { headers: { Authorization: `Bearer ${adminUser.accessToken}` } }
      );

      const { url } = response.data;
      if (url) {
        window.open(url, '_blank');
        setSnackbar({
          open: true,
          message: `${documentType} opened successfully`,
          severity: 'success',
        });
      } else {
        throw new Error('No URL returned from server');
      }
    } catch (error) {
      console.error('View document error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to open document',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (!adminUser) {
    return <Typography>Please log in as an admin to view this page.</Typography>;
  }

  return (
    <Box sx={{ p: { xs: 1, md: 2 } }}>
      <Typography variant="h4" gutterBottom>Doctor Management</Typography>

      {/* Doctor Statistics */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Doctor Statistics</Typography>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
          <Typography>Active Doctors: {doctorStats.activeDoctors}</Typography>
          <Typography>Deactivated Doctors: {doctorStats.deactivatedDoctors}</Typography>
          <Typography>Deleted Doctors: {doctorStats.deletedDoctors}</Typography>
        </Box>
      </Paper>

      {/* Search & Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Search by Name"
            value={search.name}
            onChange={(e) => setSearch({ ...search, name: e.target.value })}
            sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}
          />
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}>
            <InputLabel>Specialization</InputLabel>
            <Select
              value={search.specialization}
              onChange={(e) => setSearch({ ...search, specialization: e.target.value })}
            >
              <MenuItem value="">All Specializations</MenuItem>
              {doctorSpecializations.map((spec) => (
                <MenuItem key={spec} value={spec}>{spec}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}>
            <InputLabel>State</InputLabel>
            <Select
              value={search.state}
              onChange={(e) => setSearch({ ...search, state: e.target.value, city: '' })}
            >
              <MenuItem value="">All States</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', md: 200 } }}>
            <InputLabel>City</InputLabel>
            <Select
              value={search.city}
              onChange={(e) => setSearch({ ...search, city: e.target.value })}
            >
              <MenuItem value="">All Cities</MenuItem>
              {getAvailableCities().map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', md: 120 } }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={search.status}
              onChange={(e) => setSearch({ ...search, status: e.target.value })}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="verified">Verified</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ flex: 1, minWidth: { xs: '100%', md: 120 } }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={search.sortBy}
              onChange={(e) => setSearch({ ...search, sortBy: e.target.value })}
            >
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="consultations">Consultations</MenuItem>
              <MenuItem value="earnings">Earnings</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Pending Approvals */}
      <Paper sx={{ p: 2, mb: 3, overflowX: 'auto' }}>
        <Typography variant="h6">Pending Approvals</Typography>
        {loading && pendingDoctors.length === 0 ? (
          <CircularProgress />
        ) : (
          <Table sx={{ minWidth: { xs: 600, md: 650 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>State</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Documents</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendingDoctors.map((doc) => (
                <TableRow key={doc._id}>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>{doc.specialization}</TableCell>
                  <TableCell>{doc.state}</TableCell>
                  <TableCell>{doc.city}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 1 }}>
                      <Button
                        onClick={() => handleViewDocument(doc._id, 'medicalLicense')}
                        disabled={!doc.documents?.medicalLicense || loading}
                        size="small"
                        variant="outlined"
                      >
                        Medical License
                      </Button>
                      <Button
                        onClick={() => handleViewDocument(doc._id, 'degreeCertificate')}
                        disabled={!doc.documents?.degreeCertificate || loading}
                        size="small"
                        variant="outlined"
                      >
                        Degree Certificate
                      </Button>
                      <Button
                        onClick={() => handleViewDocument(doc._id, 'idProof')}
                        disabled={!doc.documents?.idProof || loading}
                        size="small"
                        variant="outlined"
                      >
                        ID Proof
                      </Button>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Approve Doctor">
                      <IconButton
                        onClick={() => setOpenDialog({ ...openDialog, approve: true, doctorId: doc._id })}
                        color="success"
                        disabled={loading}
                      >
                        {loading && openDialog.doctorId === doc._id && openDialog.approve ? <CircularProgress size={24} /> : <CheckCircle />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reject Doctor">
                      <IconButton
                        onClick={() => setOpenDialog({ ...openDialog, reject: true, doctorId: doc._id })}
                        color="error"
                        disabled={loading}
                      >
                        {loading && openDialog.doctorId === doc._id && openDialog.reject ? <CircularProgress size={24} /> : <Cancel />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* All Doctors */}
      <Paper sx={{ p: 2, overflowX: 'auto' }}>
        <Typography variant="h6">All Doctors</Typography>
        {loading && doctors.length === 0 ? (
          <CircularProgress />
        ) : (
          <Table sx={{ minWidth: { xs: 800, md: 1000 } }}>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Specialization</TableCell>
                <TableCell>State</TableCell>
                <TableCell>City</TableCell>
                <TableCell>Account Status</TableCell>
                <TableCell>Verification Status</TableCell>
                <TableCell>Consultations</TableCell>
                <TableCell>Gross Earnings</TableCell>
                <TableCell>Pending Payout (₹)</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDoctors.map((doc) => (
                <TableRow key={doc._id}>
                  <TableCell>{doc.name}</TableCell>
                  <TableCell>{doc.specialization}</TableCell>
                  <TableCell>{doc.state}</TableCell>
                  <TableCell>{doc.city}</TableCell>
                  <TableCell>
                    <Chip
                      label={doc.accountStatus}
                      color={doc.accountStatus === 'active' ? 'success' : doc.accountStatus === 'deactivated' ? 'warning' : 'error'}
                    />
                  </TableCell>
                  <TableCell><Chip label={doc.isVerified ? 'Verified' : 'Pending'} color={doc.isVerified ? 'success' : 'warning'} /></TableCell>
                  <TableCell>{doc.appointmentCount || 0}</TableCell>
                  <TableCell>₹{(doc.totalEarnings || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>₹{(doc.pendingPayout || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="View Details"><IconButton onClick={() => navigate(`/admin/doctors/${doc._id}`)}><Visibility /></IconButton></Tooltip>
                      <Tooltip title="Edit Profile"><IconButton onClick={() => { setSelectedDoctor(doc); setEditForm(doc); setOpenDialog({ ...openDialog, edit: true }); }}><Edit /></IconButton></Tooltip>
                      <Tooltip title="View Appointments"><IconButton onClick={() => navigate(`/admin/doctors/${doc._id}/appointments`)}><HistoryIcon /></IconButton></Tooltip>
                      <Tooltip title="Process Payout">
                        <span>
                          <IconButton
                            onClick={() => handlePayout(doc._id)}
                            disabled={loading || (doc.pendingPayout || 0) <= 0}
                          >
                            {loading && doc._id === selectedDoctor?._id ? <CircularProgress size={24} /> : <PaymentIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Approve Dialog */}
      <Dialog open={openDialog.approve} onClose={() => setOpenDialog({ ...openDialog, approve: false })}>
        <DialogTitle>Approve Doctor</DialogTitle>
        <DialogContent>Are you sure you want to approve this doctor?</DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, approve: false })} disabled={loading}>Cancel</Button>
          <Button onClick={() => handleApproveReject(openDialog.doctorId, 'approve')} color="success" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={openDialog.reject} onClose={() => setOpenDialog({ ...openDialog, reject: false })}>
        <DialogTitle>Reject Doctor</DialogTitle>
        <DialogContent>
          <TextField
            label="Rejection Reason (sent to doctor via email)"
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            fullWidth
            sx={{ mt: 2 }}
            disabled={loading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, reject: false })} disabled={loading}>Cancel</Button>
          <Button onClick={() => handleApproveReject(openDialog.doctorId, 'reject')} color="error" disabled={!rejectionReason || loading}>
            {loading ? <CircularProgress size={24} /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={openDialog.edit} onClose={() => setOpenDialog({ ...openDialog, edit: false })}>
        <DialogTitle>Edit Doctor Profile</DialogTitle>
        <DialogContent>
          <TextField label="Name" value={editForm.name || ''} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} fullWidth sx={{ mt: 2 }} disabled={loading} />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Specialization</InputLabel>
            <Select
              value={editForm.specialization || ''}
              onChange={(e) => setEditForm({ ...editForm, specialization: e.target.value })}
              disabled={loading}
            >
              <MenuItem value="">Select Specialization</MenuItem>
              {doctorSpecializations.map((spec) => (
                <MenuItem key={spec} value={spec}>{spec}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>State</InputLabel>
            <Select
              value={editForm.state || ''}
              onChange={(e) => setEditForm({ ...editForm, state: e.target.value, city: '' })}
              disabled={loading}
            >
              <MenuItem value="">Select State</MenuItem>
              {states.map((state) => (
                <MenuItem key={state} value={state}>{state}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>City</InputLabel>
            <Select
              value={editForm.city || ''}
              onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              disabled={!editForm.state || loading}
            >
              <MenuItem value="">Select City</MenuItem>
              {(editForm.state ? cities[editForm.state] || [] : []).map((city) => (
                <MenuItem key={city} value={city}>{city}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Availability</InputLabel>
            <Select
              value={editForm.isActive || false}
              onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value })}
              disabled={loading}
            >
              <MenuItem value={true}>Active</MenuItem>
              <MenuItem value={false}>Inactive</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog({ ...openDialog, edit: false })} disabled={loading}>Cancel</Button>
          <Button onClick={handleEdit} color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Success/Error Messages */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DoctorManagement;